/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  changePassword,
  getCurrentUser,
  loginUser,
  logoutUser,
  refreshApiToken,
  registerUser,
  updateDisplayName,
} from '../lib/auth';
import { bootstrapStore } from '../lib/bootstrap';
import type { PublicUser } from '../lib/types';

type AuthContextValue = {
  user: PublicUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (oldPassword: string, nextPassword: string) => Promise<void>;
  refreshToken: () => Promise<string>;
  updateName: (name: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    bootstrapStore()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) {
          setUser(getCurrentUser());
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      async login(email, password) {
        const next = await loginUser(email, password);
        setUser(next);
      },
      async register(email, password) {
        const next = await registerUser(email, password);
        setUser(next);
      },
      logout() {
        logoutUser();
        setUser(null);
      },
      async changePassword(oldPassword, nextPassword) {
        if (!user) throw new Error('请先登录');
        await changePassword(user.id, oldPassword, nextPassword);
      },
      async refreshToken() {
        if (!user) throw new Error('请先登录');
        const next = await refreshApiToken(user.id);
        setUser(next);
        return next.apiToken;
      },
      async updateName(name) {
        if (!user) throw new Error('请先登录');
        setUser(await updateDisplayName(user.id, name));
      },
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
